import { useDisclosure } from "@mantine/hooks";
import { PropsWithChildren, createContext, useContext } from "react";

import {
  CollapsableCollapse,
  CollapsableToggleActionIcon,
  CollapsableToggleButton,
} from "./components";

import type { FC } from "react";

type CollapsableContexValue = {
  opened: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

export type CollapsableProps = PropsWithChildren<{
  defaultOpen?: boolean;
}>;

type CollapsableComponent = FC<CollapsableProps> & {
  Collapse: typeof CollapsableCollapse;
  ToggleActionIcon: typeof CollapsableToggleActionIcon;
  ToggleButton: typeof CollapsableToggleButton;
};

const CollapsableContext = createContext<CollapsableContexValue>({} as CollapsableContexValue);

const CollapsableProvider: CollapsableComponent = ({ children, defaultOpen = false }) => {
  const [opened, { open, close, toggle }] = useDisclosure(defaultOpen);

  return (
    <CollapsableContext.Provider value={{ opened, open, close, toggle }}>
      {children}
    </CollapsableContext.Provider>
  );
};

CollapsableProvider.Collapse = CollapsableCollapse;
CollapsableProvider.ToggleActionIcon = CollapsableToggleActionIcon;
CollapsableProvider.ToggleButton = CollapsableToggleButton;

export const Collapsable = CollapsableProvider;

export function useCollapsable() {
  const context = useContext(CollapsableContext);
  if (!context) {
    throw new Error("useContext must be used inside a Collapsable");
  }
  return context;
}
